import {
  DropdownArrowIcon,
  DropdownArrowIconUp,
  Render,
} from 'Components/Display';
import { NavConfigItem, ROUTES, useGetSecureSubNavItems } from 'Navigation';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Card } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';
import { fullClassName, useHasPermissions, useToggler } from 'Utilities';
import './NavigationPillBar.scss';

export type NavigationPillProps = NavConfigItem &
  React.HTMLProps<HTMLDivElement>;

export const NavigationPill: React.FC<NavigationPillProps> = ({
  route,
  fullRoute,
  active,
  ...otherProps
}) => {
  const [dropdownItems, setDropdownItems] = useState<NavConfigItem[]>([]);
  const [hasSubRoutes, setHasSubRoutes] = useState<boolean>(false);
  const getSubNavItems = useGetSecureSubNavItems(undefined, true);
  const hasPermissions = useHasPermissions();
  const typedRoute: ROUTES = useMemo(() => route as ROUTES, [route]);
  const location = useLocation();

  useEffect(() => {
    if (typedRoute) {
      var unfilteredItems = getSubNavItems(typedRoute);
      setHasSubRoutes(unfilteredItems.length > 0);
      var items = unfilteredItems.filter(
        (x) => x.showInDropdown && hasPermissions(x.permissions)
      );
      setDropdownItems(items.length > 0 ? items : []);
    }
  }, [typedRoute]);

  const singleIsActive =
    !hasSubRoutes &&
    fullRoute !== undefined &&
    location.pathname === (fullRoute || ROUTES.UNAUTHORIZED);

  return (
    <Render condition={!(hasSubRoutes && dropdownItems.length === 0)}>
      {dropdownItems.length > 0 ? (
        <NavPillAccordion
          dropdownItems={dropdownItems}
          route={route}
          {...otherProps}
        />
      ) : (
        <div
          className="navpill-dropdown"
          style={{ position: 'relative', margin: '0 10px' }}
        >
          <Link to={fullRoute || ROUTES.UNAUTHORIZED} className="full-height">
            <span className={`navpill-dropdown__label`}>
              {otherProps.label}
              {singleIsActive && (
                <div className="navpill-dropdown__underline" />
              )}
            </span>
          </Link>
        </div>
      )}
    </Render>
  );
};

type NavPillContentProps = {
  label: string;
  icon?: React.ReactNode;
  isDropdown?: boolean;
};

const NavPillContent: React.FC<NavPillContentProps> = ({
  icon,
  label,
  isDropdown,
}) => {
  return (
    <div
      className={fullClassName(
        'nav-label-container flex-vertical-center header-size-tiny'
      )}
    >
      <div className="navbar__sidebar__item--icon">{icon}</div>
      {label}
      <Render condition={isDropdown}>
        <DropdownArrowIcon className="dropdown-arrow" />
      </Render>
    </div>
  );
};

type NavPillDropdownProps = NavigationPillProps & {
  dropdownItems: NavConfigItem[];
};

export const NavPillDropdown: React.FC<NavPillDropdownProps> = ({
  label,
  icon,
  route,
  dropdownItems,
}) => {
  const [isDropdownOpen, toggleDropdownOpen] = useToggler(false);

  return (
    <Dropdown
      className="navpill__wrapper flex-vertical-center"
      onMouseEnter={toggleDropdownOpen}
      onMouseLeave={toggleDropdownOpen}
      isOpen={isDropdownOpen}
      toggle={() => {}}
    >
      <DropdownToggle onClick={(e) => e.preventDefault()}>
        <NavPillContent
          label={label}
          icon={icon}
          isDropdown={dropdownItems && dropdownItems.length > 0}
        />
      </DropdownToggle>
      <DropdownMenu>
        {dropdownItems.map((item, index) => (
          <Link
            to={
              item.fullRoute ? `${route}${item.fullRoute}` : ROUTES.UNAUTHORIZED
            }
            key={index}
          >
            <DropdownItem className="flex-vertical-center">
              <div className="dropdown-icon">{item.icon}</div>
              {item.label}
            </DropdownItem>
          </Link>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};
export const NavPillAccordion: React.FC<NavPillDropdownProps> = ({
  label,
  icon,
  route,
  dropdownItems = [],
}) => {
  const [isOpen, toggleOpen] = useToggler(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const parentActive = dropdownItems.some((item) => {
    const fullPath = item.fullRoute ? `${route}${item.fullRoute}` : route;
    return location.pathname === fullPath;
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        toggleOpen();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, toggleOpen]);

  return (
    <div className="navpill-dropdown" ref={containerRef}>
      <button
        type="button"
        className="navpill-dropdown__toggle"
        onClick={toggleOpen}
      >
        <span className="navpill-dropdown__toggle-content">
          <span className="navpill-dropdown__icon">{icon}</span>
          <span className={`navpill-dropdown__label ${parentActive ? 'active' : ''}`}>{label}</span>
          <Render condition={dropdownItems.length > 0}>
            {isOpen ? (
              <DropdownArrowIconUp className="navpill-dropdown__arrow" />
            ) : (
              <DropdownArrowIcon className="navpill-dropdown__arrow" />
            )}
          </Render>
          {parentActive && <div className="navpill-dropdown__underline" />}
        </span>
      </button>

      <Render condition={isOpen && dropdownItems.length > 0}>
        <ul className="navpill-dropdown__menu">
          {dropdownItems.map((item, idx) => {
            const fullPath = item.fullRoute
              ? `${route}${item.fullRoute}`
              : route;

            return (
              <li key={idx} className="navpill-dropdown__item">
                <Link
                  to={fullPath}
                  className="navpill-dropdown__link"
                  onClick={() => {
                    // close dropdown when sub-link clicked
                    toggleOpen();
                  }}
                >
                  <span className="navpill-dropdown__item-label">
                    {item.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </Render>
    </div>
  );
};
